/**
 * GuideCallout — boxed paragraph used to draw attention to a single point.
 * Four flavours map to lightweight semantic colours so the page reads as
 * documentation, not a UI form. Used heavily across guide sections.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import { useAppTheme } from '../../hooks/useAppTheme';

export type CalloutKind = 'info' | 'tip' | 'warning' | 'danger';

interface Props {
  kind?: CalloutKind;
  title?: string;
  children: React.ReactNode;
}

const GuideCallout: React.FC<Props> = ({ kind = 'info', title, children }) => {
  const { C, isDark } = useAppTheme();
  const palette = {
    info:    { fg: C.neutral, bg: C.neutralMuted, bd: C.neutralBorder, icon: <InfoOutlinedIcon sx={{ fontSize: 16 }} />,    defaultTitle: 'Note' },
    tip:     { fg: C.bull,    bg: C.bullMuted,    bd: C.bullBorder,    icon: <TipsAndUpdatesIcon sx={{ fontSize: 16 }} />,  defaultTitle: 'Tip' },
    warning: { fg: C.warn,    bg: C.warnMuted,    bd: C.warnBorder,    icon: <WarningAmberIcon sx={{ fontSize: 16 }} />,    defaultTitle: 'Heads up' },
    danger:  { fg: C.bear,    bg: C.bearMuted,    bd: C.bearBorder,    icon: <ErrorOutlineIcon sx={{ fontSize: 16 }} />,    defaultTitle: 'Important' },
  }[kind];

  return (
    <Box
      sx={{
        my: 1.5,
        p: 1.5,
        borderLeft: `3px solid ${palette.fg}`,
        bgcolor: palette.bg,
        border: '1px solid',
        borderColor: palette.bd,
        borderRadius: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: palette.fg, mb: title || true ? 0.5 : 0 }}>
        {palette.icon}
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {title || palette.defaultTitle}
        </Typography>
      </Box>
      <Box sx={{ fontSize: '0.88rem', lineHeight: 1.6, color: isDark ? 'text.primary' : 'text.primary' }}>
        {children}
      </Box>
    </Box>
  );
};

export default GuideCallout;
